import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';  // Get the brandId from the URL
import { apiClient } from '../api/api';
import "./AdminCreatorPerformance.css";
import Loader from './Loader';

const AdminCreatorPerformance = ({ startDate, endDate, brandInfo }) => {
  const { brandId: urlBrandId } = useParams();  // Get the brandId from URL params
  const brandId = brandInfo?.brand_id || urlBrandId; // Use brandInfo if available, fallback to URL param
  const [creatorData, setCreatorData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // The formatDate function has been removed as it was unused

  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate || !brandId) return;
      
      setIsLoading(true);
      try {
        // Use the admin endpoint for creator performance
        const response = await apiClient.get('/admin/creator_performance', {
          params: {
            brand_id: brandId,
            start_date: startDate,
            end_date: endDate,
            period_type: 'day',  // Could be made dynamic based on date range
            page_size: 20,
            page_no: 1
          }
        });
        // Set the creator performance data state
        setCreatorData(response.data.results || []);
        setIsLoading(false);
  
      } catch (error) {
        console.error("Error fetching creator performance data:", error);
        setIsLoading(false);
      }
    };
  
    fetchData();
  }, [startDate, endDate, brandId]);
  
  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Find all unique creators across all periods
  const getAllCreators = () => {
    const creators = [];
    
    creatorData.forEach(period => {
      period.top_creators?.forEach(creator => {
        const existingCreator = creators.find(c => c.creator_id === creator.creator_id);
        if (!existingCreator) {
          creators.push({
            creator_id: creator.creator_id,
            creator_name: creator.creator_name,
            total_sales: creator.sales || 0,
            total_views: creator.views || 0,
            total_videos: creator.videos || 0
          });
        } else {
          existingCreator.total_sales += creator.sales || 0;
          existingCreator.total_views += creator.views || 0;
          existingCreator.total_videos += creator.videos || 0;
        }
      });
    });
    
    // Sort by total sales
    return creators.sort((a, b) => b.total_sales - a.total_sales);
  };
  
  const topCreators = getAllCreators().slice(0, 10);  // Get top 10 creators

  return (
    <div className="creator-performance-container">
      <h2>Affiliate & Content Performance</h2>
      
      {isLoading ? (
        <Loader text="Loading creator performance data..." />
      ) : (
        <div className="creator-data-container">
          <div className="creator-metrics-summary">
            <div className="metric-card">
              <h3>Total Creators</h3>
              <p className="metric-value">{topCreators.length}</p>
            </div>
            <div className="metric-card">
              <h3>Total Sales</h3>
              <p className="metric-value">
                {topCreators.length === 0 ? 
                  formatCurrency(0) :
                  formatCurrency(topCreators.reduce((sum, creator) => sum + creator.total_sales, 0))
                }
              </p>
            </div>
            <div className="metric-card">
              <h3>Total Videos</h3>
              <p className="metric-value">
                {topCreators.length === 0 ? 
                  '0' :
                  topCreators.reduce((sum, creator) => sum + creator.total_videos, 0)
                }
              </p>
            </div>
          </div>
          
          <div className="top-creators-table">
            <h3>Top Performing Creators</h3>
            <table className="creators-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Creator</th>
                  <th>Sales</th>
                  <th>Views</th>
                  <th>Videos</th>
                </tr>
              </thead>
              <tbody>
                {topCreators.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="no-data-message">No creator data available for the selected date range.</td>
                  </tr>
                ) : (
                  topCreators.map((creator, index) => (
                    <tr key={creator.creator_id}>
                      <td>{index + 1}</td>
                      <td>{creator.creator_name}</td>
                      <td>{formatCurrency(creator.total_sales)}</td>
                      <td>{creator.total_views.toLocaleString()}</td>
                      <td>{creator.total_videos}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCreatorPerformance;